import { OnboardingWizardState } from '../context/OnboardingWizardContext'

// Version for localStorage schema - increment when making breaking changes
const STORAGE_VERSION = 1

// Storage key prefix
const STORAGE_KEY_PREFIX = 'onboarding_wizard_state'

// Expiration time for stored data (7 days in milliseconds)
const STORAGE_EXPIRATION = 7 * 24 * 60 * 60 * 1000

interface CompetitionFormData {
  name: string
  selectedLeague: string | null
  selectedSport: string | null
  startDate: string | null
  endDate: string | null
}

interface StoredWizardData {
  version: number
  timestamp: number
  state: OnboardingWizardState
  competitionFormData?: CompetitionFormData
}

/**
 * Get storage key for a specific user
 * @param userId - User ID to create unique storage key
 * @returns Storage key string
 */
const getStorageKey = (userId: string | number): string => {
  return `${STORAGE_KEY_PREFIX}_${userId}`
}

/**
 * Check if stored data is expired
 * @param timestamp - Timestamp when data was stored
 * @returns True if data is expired
 */
const isDataExpired = (timestamp: number): boolean => {
  return Date.now() - timestamp > STORAGE_EXPIRATION
}

/**
 * Validate stored wizard state structure
 * @param state - State object to validate
 * @returns True if state is valid
 */
const validateWizardState = (state: any): state is OnboardingWizardState => {
  return (
    state &&
    typeof state === 'object' &&
    typeof state.currentStep === 'number' &&
    state.currentStep >= 0 &&
    state.currentStep <= 3 &&
    (state.leagueType === null ||
      state.leagueType === 'simple' ||
      state.leagueType === 'complex') &&
    Array.isArray(state.sportsData)
  )
}

/**
 * Validate stored competition form data structure
 * @param data - Data object to validate
 * @returns True if data is valid
 */
const validateCompetitionFormData = (
  data: any
): data is CompetitionFormData => {
  return (
    data &&
    typeof data === 'object' &&
    typeof data.name === 'string' &&
    (data.selectedLeague === null || typeof data.selectedLeague === 'string') &&
    (data.selectedSport === null || typeof data.selectedSport === 'string') &&
    (data.startDate === null || typeof data.startDate === 'string') &&
    (data.endDate === null || typeof data.endDate === 'string')
  )
}

/**
 * Save wizard state to localStorage
 * @param userId - User ID for unique storage
 * @param state - Wizard state to save
 * @param competitionFormData - Optional competition form data
 */
export const saveWizardState = (
  userId: string | number,
  state: OnboardingWizardState,
  competitionFormData?: CompetitionFormData
): void => {
  try {
    console.log('🔄 localStorage: Saving wizard state', {
      userId,
      state,
      competitionFormData,
    })

    const storageKey = getStorageKey(userId)
    const dataToStore: StoredWizardData = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      state,
      competitionFormData,
    }

    localStorage.setItem(storageKey, JSON.stringify(dataToStore))
    console.log('✅ localStorage: Wizard state saved successfully')
  } catch (error) {
    console.error('🚨 localStorage: Error saving wizard state:', error)
  }
}

/**
 * Load wizard state from localStorage
 * @param userId - User ID for unique storage
 * @returns Stored wizard data or null if not found/invalid
 */
export const loadWizardState = (
  userId: string | number
): {
  state: OnboardingWizardState
  competitionFormData?: CompetitionFormData
} | null => {
  try {
    console.log('🔄 localStorage: Loading wizard state for user', userId)

    const storageKey = getStorageKey(userId)
    const storedData = localStorage.getItem(storageKey)

    if (!storedData) {
      console.log('⚠️ localStorage: No stored wizard state found')
      return null
    }

    const parsedData: StoredWizardData = JSON.parse(storedData)

    // Check version compatibility
    if (parsedData.version !== STORAGE_VERSION) {
      console.warn(
        `⚠️ localStorage: Version mismatch (stored: ${parsedData.version}, current: ${STORAGE_VERSION}), clearing data`
      )
      clearWizardState(userId)
      return null
    }

    // Check if data is expired
    if (isDataExpired(parsedData.timestamp)) {
      console.warn('⚠️ localStorage: Stored data is expired, clearing')
      clearWizardState(userId)
      return null
    }

    // Validate state structure
    if (!validateWizardState(parsedData.state)) {
      console.warn('⚠️ localStorage: Invalid state structure, clearing data')
      clearWizardState(userId)
      return null
    }

    // Validate competition form data if present
    if (
      parsedData.competitionFormData &&
      !validateCompetitionFormData(parsedData.competitionFormData)
    ) {
      console.warn('⚠️ localStorage: Invalid competition form data, clearing')
      delete parsedData.competitionFormData
    }

    console.log('✅ localStorage: Wizard state loaded successfully', parsedData)
    return {
      state: parsedData.state,
      competitionFormData: parsedData.competitionFormData,
    }
  } catch (error) {
    console.error('🚨 localStorage: Error loading wizard state:', error)
    // Clear corrupted data
    clearWizardState(userId)
    return null
  }
}

/**
 * Clear wizard state from localStorage
 * @param userId - User ID for unique storage
 */
export const clearWizardState = (userId: string | number): void => {
  try {
    console.log('🔄 localStorage: Clearing wizard state for user', userId)

    const storageKey = getStorageKey(userId)
    localStorage.removeItem(storageKey)

    console.log('✅ localStorage: Wizard state cleared successfully')
  } catch (error) {
    console.error('🚨 localStorage: Error clearing wizard state:', error)
  }
}

/**
 * Check if there is any saved wizard state for a user
 * @param userId - User ID to check
 * @returns True if saved state exists and is valid
 */
export const hasSavedWizardState = (userId: string | number): boolean => {
  const savedData = loadWizardState(userId)
  return savedData !== null
}

/**
 * Clean up all expired wizard states from localStorage
 * This function scans all localStorage keys for wizard states and removes expired ones
 */
export const cleanupExpiredWizardStates = (): void => {
  try {
    console.log('🔄 localStorage: Cleaning up expired wizard states')
    let cleanedCount = 0

    // Get all localStorage keys
    const keys = Object.keys(localStorage)

    // Filter keys that match our wizard state pattern
    const wizardKeys = keys.filter((key) => key.startsWith(STORAGE_KEY_PREFIX))

    wizardKeys.forEach((key) => {
      try {
        const storedData = localStorage.getItem(key)
        if (storedData) {
          const parsedData: StoredWizardData = JSON.parse(storedData)

          // Check if data is expired or has invalid version
          if (
            isDataExpired(parsedData.timestamp) ||
            parsedData.version !== STORAGE_VERSION
          ) {
            localStorage.removeItem(key)
            cleanedCount++
            console.log(
              `🧹 localStorage: Removed expired/invalid data for key: ${key}`
            )
          }
        }
      } catch (error) {
        // If we can't parse the data, it's corrupted - remove it
        localStorage.removeItem(key)
        cleanedCount++
        console.log(`🧹 localStorage: Removed corrupted data for key: ${key}`)
      }
    })

    if (cleanedCount > 0) {
      console.log(
        `✅ localStorage: Cleaned up ${cleanedCount} expired/invalid wizard states`
      )
    } else {
      console.log('✅ localStorage: No expired wizard states found')
    }
  } catch (error) {
    console.error('🚨 localStorage: Error during cleanup:', error)
  }
}

/**
 * Get summary of all wizard states in localStorage (for debugging)
 * @returns Array of wizard state summaries
 */
export const getWizardStatesSummary = (): Array<{
  userId: string
  currentStep: number
  timestamp: Date
  isExpired: boolean
}> => {
  try {
    const keys = Object.keys(localStorage)
    const wizardKeys = keys.filter((key) => key.startsWith(STORAGE_KEY_PREFIX))

    return wizardKeys
      .map((key) => {
        try {
          const userId = key.replace(`${STORAGE_KEY_PREFIX}_`, '')
          const storedData = localStorage.getItem(key)
          if (storedData) {
            const parsedData: StoredWizardData = JSON.parse(storedData)
            return {
              userId,
              currentStep: parsedData.state.currentStep,
              timestamp: new Date(parsedData.timestamp),
              isExpired: isDataExpired(parsedData.timestamp),
            }
          }
        } catch (error) {
          console.error(`Error parsing wizard state for key ${key}:`, error)
        }
        return null
      })
      .filter(Boolean) as Array<{
      userId: string
      currentStep: number
      timestamp: Date
      isExpired: boolean
    }>
  } catch (error) {
    console.error(
      '🚨 localStorage: Error getting wizard states summary:',
      error
    )
    return []
  }
}

// Export types for use in other components
export type { CompetitionFormData, StoredWizardData }
